import React from 'react';
import { Dialog } from '@headlessui/react';
import { useClub } from '../context/ClubContext';
import { useAuth } from '../context/AuthContext';
import { getUserTypes, purchaseCredits } from '../api';
import LightCreditsModal from './LightCreditsModal';
import PaymentModal from './PaymentModal';
import Tooltip from './Tooltip';
import { formatSportDisplayName } from '../utils/sportName';
import { getCourtDisplayNameById } from '../utils/courtDisplay';
import { buildCreditReference } from '../utils/paymentReference';

const BookingModal = ({
  isOpen,
  onClose,
  onSubmit,
  slotData,
  isEditing = false,
  courts,
  uniqueSportsCount,
  editMode = 'full',
  existingBookings = [],
  previewMode = false,
  previewUser = null,
  previewUserTypeName = '',
  bookingCategoriesOverride = null,
  bookingOptionsOverride = null
}) => {
  const { getCategoryStyle, getCategoryStyleFromList, bookingCategories, bookingOptions, courtData } = useClub();
  const effectiveBookingCategories = bookingCategoriesOverride || bookingCategories;
  const effectiveBookingOptions = bookingOptionsOverride || bookingOptions;
  const { user, loginUser } = useAuth();
  /** When previewing from Club Config, permission/fee fields match the edited user type; identity stays the logged-in user. */
  const effectiveUser = previewMode && previewUser ? previewUser : user;
  const [errors, setErrors] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [userTypes, setUserTypes] = React.useState([]);
  const [isPublic, setIsPublic] = React.useState(false);
  const [publicSpaces, setPublicSpaces] = React.useState(1);
  const [publicFees, setPublicFees] = React.useState({});
  const [showLightCreditsModal, setShowLightCreditsModal] = React.useState(false);
  const [showCreditPaymentModal, setShowCreditPaymentModal] = React.useState(false);
  const [creditPaymentDetails, setCreditPaymentDetails] = React.useState(null);
  const creditPaymentDetailsRef = React.useRef(null);

  const handleCreditPaymentComplete = React.useCallback(async () => {
    const paymentDetails = creditPaymentDetailsRef.current;
    if (!paymentDetails) return;
    
    try {
      // Call the purchase credits API with reference and credit amount
      const response = await purchaseCredits(paymentDetails.reference, paymentDetails.creditAmount);
      
      // Update the token using loginUser to refresh user data
      if (response?.token) {
        loginUser(response.token);
      }
      
      // Close payment modal but keep LightCreditsModal open
      setShowCreditPaymentModal(false);
      creditPaymentDetailsRef.current = null;
      setCreditPaymentDetails(null);
      
      // The user data will be updated via loginUser, so the modal will show updated credits
    } catch (error) {
      console.error('Error purchasing credits:', error);
      // Close payment modal first to prevent multiple QR codes
      setShowCreditPaymentModal(false);
      // Show error in existing errors state
      setErrors({ submit: `Failed to purchase credits, please contact the club with Reference: ${paymentDetails?.reference || 'N/A'}` });
      creditPaymentDetailsRef.current = null;
      setCreditPaymentDetails(null);
    }
  }, [loginUser]);

  const handleCreditPaymentModalClose = React.useCallback(() => {
    // Just close the payment modal, keep LightCreditsModal open
    setShowCreditPaymentModal(false);
    creditPaymentDetailsRef.current = null;
    setCreditPaymentDetails(null);
  }, []);
  
  // User permissions for the court type
  const court = Array.isArray(slotData.court) ? slotData.court[0] : slotData.court; // Use the first element if it's an array
  console.log("*********** slotData", slotData);
  console.log("*********** slotData.court", slotData.court);
  const courtType = court.split('-')[0]; // Determine court type (eg: tennis or pickleball)
  const sportDisplayName = formatSportDisplayName(courtType);
  const courtPermissions = effectiveUser?.permissions[courtType] || {};
  const canBook = courtPermissions.canBook || false;
  const canBookMultipleCourts = courtPermissions.canBookMultipleCourts || false;
  const canBookExtras = courtPermissions.canBookExtras || false;
  const canBookRecurring = courtPermissions.canBookRecurring || [];
  const canMakePublicBookings = courtPermissions.canMakePublicBookings || false;
  const bookingExtrasPermissions = Array.isArray(courtPermissions.bookingExtras)
    ? courtPermissions.bookingExtras
    : [];

  // Get categories for the current court type, sorted by ID
  const sportCategories = React.useMemo(() => {
    const enabledCategories = courtPermissions.bookingCategories || []; // Get the enabled category IDs
    return effectiveBookingCategories
      .filter(cat => 
        cat.type === courtType && 
        enabledCategories.includes(cat.id) && // Only include enabled categories
        cat.name !== 'Available' && 
        cat.name !== 'Recurring'
      )
      .sort((a, b) => parseInt(a.id) - parseInt(b.id));
  }, [effectiveBookingCategories, courtType, courtPermissions]);

  // Track selected category by ID for efficient payloads/backend lookups.
  const [categoryId, setCategoryId] = React.useState(() => {
    if (slotData.category) {
      const existingCategory = sportCategories.find(cat => cat.name === slotData.category);
      if (existingCategory) return existingCategory.id;
    }
    return sportCategories[0]?.id || '';
  });
  const [comments, setComments] = React.useState(slotData.comments || '');
  const [displayLabel, setDisplayLabel] = React.useState(slotData.displayLabel || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`);
  const [isRecurring, setIsRecurring] = React.useState(slotData.isRecurring || false);
  const [selectedExtras, setSelectedExtras] = React.useState([]);

  const availableExtras = React.useMemo(() => {
    const nameMap = (effectiveBookingOptions || []).reduce((acc, option) => {
      if (option?.option) {
        acc[option.option] = option.name || option.option;
      }
      return acc;
    }, {});
    return bookingExtrasPermissions.map(extra => ({
      option: extra.option,
      name: nameMap[extra.option] || extra.option,
      fee: parseFloat(extra.fee ?? 0) || 0
    }));
  }, [bookingExtrasPermissions, effectiveBookingOptions]);

  const getCategoryStyleForModal = React.useCallback(
    ({ category, court }) =>
      getCategoryStyleFromList({
        category,
        court,
        categories: effectiveBookingCategories
      }) || getCategoryStyle({ category, court }),
    [effectiveBookingCategories, getCategoryStyleFromList, getCategoryStyle]
  );
  
  // Reduce exclude courts that are not the same court type from multi court bookings
  console.log("*********** courts", courts);
  courts = courts.filter(court => court.court.split('-')[0] === courtType);
  console.log("*********** courts", courts);
  const hasAnyDisplayNameInSport = courts.some((courtItem) => String(courtItem?.displayName || '').trim().length > 0);

  console.log("*********** canBook", canBook);
  console.log("*********** canBookMultipleCourts", canBookMultipleCourts);
  console.log("*********** canBookRecurring", canBookRecurring);  

  // Parse recurringRule if it's a string
  const parsedRecurringRule = React.useMemo(() => {
    if (!slotData.recurringRule) return {};
    try {
      return typeof slotData.recurringRule === 'string' 
        ? JSON.parse(slotData.recurringRule)
        : slotData.recurringRule;
    } catch (e) {
      console.error('Error parsing recurring rule:', e);
      return {};
    }
  }, [slotData.recurringRule]);

  // Set recurringFrequency and recurringDuration based on the new requirements
  // Default to weekly if available, otherwise use the first available option
  const getDefaultFrequency = () => {
    if (canBookRecurring.length === 0) return 'weekly';
    const weeklyOption = canBookRecurring.find(option => option.frequency === 'weekly');
    if (weeklyOption) return 'weekly';
    return canBookRecurring[0].frequency;
  };

  // Get initial duration based on frequency - if block is enabled, default to -1, otherwise 2
  const getInitialDuration = (frequency) => {
    const durationOption = canBookRecurring.find(option => option.frequency === frequency);
    if (durationOption && durationOption.block) {
      return -1; // Never Ending
    }
    return 2; // Default to 2
  };

  const defaultFrequency = parsedRecurringRule.frequency || getDefaultFrequency();
  const [recurringFrequency, setRecurringFrequency] = React.useState(defaultFrequency);

  // Set the initial recurringDuration - if block is enabled for the frequency, default to -1, otherwise 2
  const [recurringDuration, setRecurringDuration] = React.useState(
    parsedRecurringRule.duration || getInitialDuration(defaultFrequency)
  );

  // Initialize selectedCourts as an array, even if slotData.court is a single string
  const [selectedCourts, setSelectedCourts] = React.useState([]);
  const [endTime, setEndTime] = React.useState(slotData.endTime);

  // Calculate current joiners for validation
  const currentJoiners = React.useMemo(() => {
    if (!isEditing || !slotData.public) return 0;
    try {
      const publicData = typeof slotData.public === 'string' ? JSON.parse(slotData.public) : slotData.public;
      const pendingCount = Object.keys(publicData.pending || {}).length;
      const confirmedCount = Object.keys(publicData.confirmed || {}).length;
      return pendingCount + confirmedCount;
    } catch {
      return 0;
    }
  }, [isEditing, slotData.public]);

  // Check if there are existing joiners
  const hasExistingJoiners = currentJoiners > 0;
  
  // Check if ANY booking in the group has joiners (for recurring series restriction)
  const hasJoinersInGroup = React.useMemo(() => {
    if (!isEditing || !slotData.bookingId) {
      return hasExistingJoiners;
    }
    
    // For now, we'll use the current booking's joiner status
    // In a more sophisticated implementation, we could check all bookings in the group
    return hasExistingJoiners;
  }, [isEditing, slotData.bookingId, hasExistingJoiners]);
  
  // Check if this is a multi-court booking for the same day (allows editing all courts for that day)
  const isMultiCourtSameDay = React.useMemo(() => {
    if (!isEditing || !slotData.bookingId || !Array.isArray(slotData.court)) {
      return false;
    }
    
    // This is a multi-court booking for the same day
    return slotData.court.length > 1;
  }, [isEditing, slotData.bookingId, slotData.court]);

  // Determine if this is individual edit mode (has joiners or other bookings in group have joiners)
  const isIndividualEditMode = isEditing && editMode === 'individual';
  
  // For individual edit mode, we need to preserve existing public data
  const existingPublicData = React.useMemo(() => {
    if (!isIndividualEditMode || !slotData.public) return null;
    try {
      return typeof slotData.public === 'string' ? JSON.parse(slotData.public) : slotData.public;
    } catch {
      return null;
    }
  }, [isIndividualEditMode, slotData.public]);

  const getCourtFeePerSlot = React.useCallback((courtId) => {
    if (!courtId || !effectiveUser?.permissions) return 0;
    const courtTypeKey = String(courtId).split('-')[0];
    const typePermissions = effectiveUser.permissions[courtTypeKey] || {};
    const feeValue = parseFloat(typePermissions.feePerSlot ?? 0);
    return isNaN(feeValue) ? 0 : feeValue;
  }, [effectiveUser?.permissions]);

  const hasPaidCourtsSelected = React.useMemo(() => {
    const courtsToEvaluate = ((selectedCourts && selectedCourts.length > 0)
      ? selectedCourts
      : (Array.isArray(slotData.court) ? slotData.court : [slotData.court])
    ).filter(Boolean);

    return courtsToEvaluate.some(courtId => getCourtFeePerSlot(courtId) > 0);
  }, [selectedCourts, slotData.court, getCourtFeePerSlot]);

  const selectedExtrasTotal = React.useMemo(() => {
    return selectedExtras.reduce((sum, extra) => {
      const feeValue = parseFloat(extra.fee ?? 0);
      const qtyValue = parseInt(extra.quantity ?? 0, 10);
      const fee = isNaN(feeValue) ? 0 : feeValue;
      const qty = isNaN(qtyValue) || qtyValue < 1 ? 0 : qtyValue;
      return sum + (fee * qty);
    }, 0);
  }, [selectedExtras]);

  const selectedCategory = React.useMemo(() => {
    if (!categoryId) return null;
    return sportCategories.find(cat => cat.id === categoryId) || null;
  }, [sportCategories, categoryId]);

  const selectedCategoryName = selectedCategory?.name || '';

  const categoryFeeMultiplier = React.useMemo(() => {
    const rawMultiplier = selectedCategory?.feeMultiplier ?? selectedCategory?.feeMuliplier ?? 1;
    const parsedMultiplier = Number(rawMultiplier);
    if (!Number.isFinite(parsedMultiplier) || parsedMultiplier < 0) return 1;
    return parsedMultiplier;
  }, [selectedCategory]);

  const blockFeeRestriction = React.useMemo(() => {
    return Boolean(isRecurring && recurringDuration === -1 && (hasPaidCourtsSelected || selectedExtrasTotal > 0));
  }, [isRecurring, recurringDuration, hasPaidCourtsSelected, selectedExtrasTotal]);

  // Validation for court/time conflicts
  const validateCourtTimeConflicts = React.useCallback((courts, startTime, endTime, bookingDate) => {
    if (!existingBookings.length) return null;

    const conflicts = [];
    
    courts.forEach(court => {
      const conflictingBooking = existingBookings.find(booking => 
        booking.court === court &&
        booking.bookingDate === bookingDate &&
        // For edit mode, exclude the current booking being edited
        (!isEditing || booking.bookingId !== slotData.bookingId) &&
        // Check for time overlap
        (booking.startTime < endTime) && (booking.endTime > startTime)
      );
      
      if (conflictingBooking) {
        conflicts.push({
          court,
          conflictingBooking: {
            startTime: conflictingBooking.startTime,
            endTime: conflictingBooking.endTime,
            createdByName: conflictingBooking.createdByName
          }
        });
      }
    });

    return conflicts.length > 0 ? conflicts : null;
  }, [existingBookings, isEditing, slotData.bookingId]);

  // State for validation errors
  const [validationErrors, setValidationErrors] = React.useState(null);

  // Fetch user types if user has public booking permission
  React.useEffect(() => {
    const fetchUserTypesIfNeeded = async () => {
      if (!previewMode && canMakePublicBookings && userTypes.length === 0) {
        try {
          const types = await getUserTypes();
          setUserTypes(types);
        } catch (error) {
          console.error('Failed to fetch user types:', error);
        }
      }
    };
    fetchUserTypesIfNeeded();
  }, [previewMode, canMakePublicBookings, userTypes.length]);

  const handleExtraToggle = (optionId, checked) => {
    if (!optionId) return;
    if (checked) {
      setSelectedExtras(prev => {
        if (prev.some(extra => extra.option === optionId)) return prev;
        return [
          ...prev,
          {
            option: optionId,
            fee: availableExtras.find(extra => extra.option === optionId)?.fee ?? 0,
            quantity: 1
          }
        ];
      });
    } else {
      setSelectedExtras(prev => prev.filter(extra => extra.option !== optionId));
    }
  };

  const handleExtraQuantityChange = (optionId, value) => {
    if (value === '') {
      setSelectedExtras(prev =>
        prev.map(extra =>
          extra.option === optionId ? { ...extra, quantity: '' } : extra
        )
      );
      return;
    }
    const quantityValue = parseInt(value, 10);
    const normalizedQuantity = isNaN(quantityValue) || quantityValue < 1 ? 1 : quantityValue;
    setSelectedExtras(prev =>
      prev.map(extra =>
        extra.option === optionId ? { ...extra, quantity: normalizedQuantity } : extra
      )
    );
  };


  // Validate conflicts when form data changes
  React.useEffect(() => {
    if (selectedCourts.length > 0 && endTime && existingBookings.length > 0) {
      const conflicts = validateCourtTimeConflicts(selectedCourts, slotData.startTime, endTime, slotData.date);
      setValidationErrors(conflicts);
    } else {
      setValidationErrors(null);
    }
  }, [selectedCourts, endTime, slotData.startTime, slotData.date, existingBookings.length, validateCourtTimeConflicts]);

  // Ensure that the state is updated when slotData changes (for editing)
  React.useEffect(() => {
    if (slotData) {
      // Set categoryId from existing category name when editing, otherwise use first available category
      if (slotData.category) {
        const existingCategory = sportCategories.find(cat => cat.name === slotData.category);
        if (existingCategory) {
          setCategoryId(existingCategory.id);
        } else {
          setCategoryId(sportCategories[0]?.id || '');
        }
      } else {
        setCategoryId(sportCategories[0]?.id || '');
      }
      setComments(slotData.comments || '');
      setDisplayLabel(slotData.displayLabel || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`);
      setIsRecurring(slotData.isRecurring || false);
      
      // Ensure court is always a flat array of strings
      const courtsArray = Array.isArray(slotData.court) ? slotData.court.flat() : [slotData.court];
      setSelectedCourts(courtsArray.map(court => String(court))); // Ensure it's a flat array of strings
      setEndTime(slotData.endTime);

      // Update recurring rule fields if they exist
      if (slotData.recurringRule) {
        try {
          const rule = typeof slotData.recurringRule === 'string' 
            ? JSON.parse(slotData.recurringRule)
            : slotData.recurringRule;
          const frequency = rule.frequency || getDefaultFrequency();
          setRecurringFrequency(frequency);
          setRecurringDuration(rule.duration !== undefined ? rule.duration : getInitialDuration(frequency));
        } catch (e) {
          console.error('Error parsing recurring rule:', e);
        }
      }

      // Update public booking fields if they exist
      if (slotData.public) {
        try {
          const publicData = typeof slotData.public === 'string' 
            ? JSON.parse(slotData.public)
            : slotData.public;
          setIsPublic(true);
          setPublicSpaces(publicData.max || 1);
          
          // Handle both old array format and new object format for backward compatibility
          let feesObj = {};
          if (publicData.fee) {
            if (Array.isArray(publicData.fee)) {
              // Old format: [["0",100],["1",0]]
              publicData.fee.forEach(([userTypeId, fee]) => {
                feesObj[userTypeId] = fee;
              });
            } else if (typeof publicData.fee === 'object') {
              // New format: {"0": 100, "1": 0}
              feesObj = publicData.fee;
            }
          }
          setPublicFees(feesObj);
        } catch (e) {
          console.error('Error parsing public booking data:', e);
        }
      }

      if (slotData.options) {
        try {
          const parsedOptions = typeof slotData.options === 'string'
            ? JSON.parse(slotData.options)
            : slotData.options;
          if (Array.isArray(parsedOptions)) {
            setSelectedExtras(parsedOptions.map(option => ({
              option: option.option,
              fee: parseFloat(option.fee ?? 0) || 0,
              quantity: parseInt(option.quantity ?? 1, 10) || 1
            })));
          } else {
            setSelectedExtras([]);
          }
        } catch (e) {
          console.error('Error parsing booking options:', e);
          setSelectedExtras([]);
        }
      } else {
        setSelectedExtras([]);
      }
    }
  }, [slotData]);

  // Check which selected courts support smart light operations
  const courtsWithSwitches = React.useMemo(() => {
    if (!selectedCourts || selectedCourts.length === 0) return [];
    
    return selectedCourts.filter(courtId => {
      const courtInfo = courts.find(c => c.court === courtId);
      return courtInfo && courtInfo.switches && courtInfo.switches.length > 0;
    });
  }, [selectedCourts, courts]);

  const getCourtLabel = React.useCallback((courtId) => {
    const fallbackPrefix = uniqueSportsCount === 1
      ? 'Court'
      : formatSportDisplayName(String(courtId || '').split('-')[0]);
    return getCourtDisplayNameById(courtId, courts, fallbackPrefix);
  }, [courts, uniqueSportsCount]);

  // Format the smart lights message
  const smartLightsMessage = React.useMemo(() => {
    if (courtsWithSwitches.length === 0) return null;
    const aliasLabels = courtsWithSwitches.map((courtId) => getCourtDisplayNameById(courtId, courts, '').trim());
    const hasAnyAlias = aliasLabels.some(Boolean);
    
    // If only one court has switches, check if multiple courts are selected
    if (courtsWithSwitches.length === 1) {
      const courtNumber = courtsWithSwitches[0].split('-')[1];
      const singleLabel = hasAnyAlias ? (aliasLabels[0] || getCourtLabel(courtsWithSwitches[0])) : `Court ${courtNumber}`;
      // If multiple courts are selected but only one has switches, specify which court
      if (selectedCourts.length > 1) {
        return `${singleLabel} supports smart light operations`;
      } else {
        return 'This court supports smart light operations';
      }
    } else {
      if (!hasAnyAlias) {
        const courtNumbers = courtsWithSwitches.map(courtId => courtId.split('-')[1]);
        if (courtNumbers.length === 2) {
          return `Courts ${courtNumbers[0]} & ${courtNumbers[1]} support smart light switches`;
        }
        const numbers = [...courtNumbers];
        const lastCourt = numbers.pop();
        return `Courts ${numbers.join(', ')} & ${lastCourt} support smart light switches`;
      }

      const courtLabels = courtsWithSwitches.map((courtId, idx) => aliasLabels[idx] || getCourtLabel(courtId));
      if (courtLabels.length === 2) {
        return `${courtLabels[0]} & ${courtLabels[1]} support smart light switches`;
      } else {
        const labels = [...courtLabels];
        const lastCourt = labels.pop();
        return `${labels.join(', ')} & ${lastCourt} support smart light switches`;
      }
    }
  }, [courtsWithSwitches, selectedCourts, getCourtLabel, courts]);

  const bookingPrice = React.useMemo(() => {
    if (!endTime) return 0;
    const courtsToPrice = ((selectedCourts && selectedCourts.length > 0)
      ? selectedCourts
      : (Array.isArray(slotData.court) ? slotData.court : [slotData.court])
    ).filter(Boolean);

    if (courtsToPrice.length === 0) return 0;

    const [startHour, startMinute] = slotData.startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    const startMinutes = (startHour * 60) + startMinute;
    const endMinutes = (endHour * 60) + endMinute;
    const durationMinutes = endMinutes - startMinutes;
    if (durationMinutes <= 0) return 0;

    // Court fees scale with the number of courts booked. Extras are charged ONCE
    // per booking day (shared across all courts on that day), so they are added a
    // single time rather than per court.
    const courtFeesTotal = courtsToPrice.reduce((sum, courtId) => {
      const courtInfo = courts.find(c => c.court === courtId);
      const slotDuration = parseInt(courtInfo?.slotDuration || '60', 10);
      const numberOfSlots = Math.max(0, durationMinutes / slotDuration);
      const courtFeePerSlot = getCourtFeePerSlot(courtId);
      return sum + (courtFeePerSlot * numberOfSlots * categoryFeeMultiplier);
    }, 0);

    const basePrice = courtFeesTotal + selectedExtrasTotal;

    // For recurring series the extras are needed on each occurrence, so the whole
    // per-day price (court fees + extras) is multiplied by the number of occurrences.
    const canApplyRecurring = isRecurring && !isIndividualEditMode && !(isEditing && hasJoinersInGroup);
    const recurrenceMultiplier = canApplyRecurring && recurringDuration > 1 ? recurringDuration : 1;
    return basePrice * recurrenceMultiplier;
  }, [
    selectedCourts,
    slotData.court,
    slotData.startTime,
    endTime,
    courts,
    getCourtFeePerSlot,
    selectedExtrasTotal,
    categoryFeeMultiplier,
    isRecurring,
    recurringDuration,
    isIndividualEditMode,
    isEditing,
    hasJoinersInGroup
  ]);

  const originalPaidFee = React.useMemo(() => {
    if (!isEditing || slotData?.status !== 'confirmed') return 0;
    const fallbackFee = parseFloat(slotData.bookingFee || 0);
    const safeFallback = isNaN(fallbackFee) ? 0 : fallbackFee;

    const bookingDateKey = slotData.bookingDate ?? slotData.date;
    const canAggregateDayGroup = slotData?.bookingId && Array.isArray(existingBookings);
    const dayGroupTotal = canAggregateDayGroup
      ? existingBookings
          .filter(booking =>
            booking.bookingId === slotData.bookingId &&
            (booking.bookingDate ?? booking.date) === bookingDateKey &&
            booking.startTime === slotData.startTime &&
            booking.endTime === slotData.endTime
          )
          .reduce((sum, booking) => {
            const feeValue = parseFloat(booking.bookingFee || 0);
            return sum + (isNaN(feeValue) ? 0 : feeValue);
          }, 0)
      : 0;

    const baseGroupFee = dayGroupTotal > 0 ? dayGroupTotal : safeFallback;
    const originalDuration = parsedRecurringRule?.duration ?? 1;
    const canApplyRecurring =
      isRecurring && !isIndividualEditMode && !(isEditing && hasJoinersInGroup);
    const recurrenceMultiplier =
      canApplyRecurring && originalDuration > 1 ? originalDuration : 1;

    return baseGroupFee * recurrenceMultiplier;
  }, [
    isEditing,
    slotData?.status,
    slotData?.bookingFee,
    slotData?.bookingId,
    slotData?.bookingDate,
    slotData?.date,
    slotData?.startTime,
    slotData?.endTime,
    existingBookings,
    parsedRecurringRule?.duration,
    isRecurring,
    isIndividualEditMode,
    hasJoinersInGroup
  ]);

  const effectiveUpdateFee = isEditing && slotData?.status === 'confirmed'
    ? bookingPrice - (isNaN(originalPaidFee) ? 0 : originalPaidFee)
    : bookingPrice;

  // Render message if user does not have booking permissions
  if (!canBook) {
    return (
      <Dialog open={isOpen} onClose={onClose} className={`fixed inset-0 overflow-y-auto ${previewMode ? 'z-[70]' : 'z-50'}`}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
          <Dialog.Panel className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-4">
            <p>You do not have permissions to make a booking.</p>
            <div className="flex justify-end gap-3 pt-1 border-slate-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (previewMode) return;
    if (isLoading) return; // Prevent double submission
    setIsLoading(true);

    if (blockFeeRestriction) {
      setIsLoading(false);
      return;
    }

    // Validate display label
    if (!displayLabel.trim()) {
      setErrors({ displayLabel: 'Calendar booking display label is required' });
      setIsLoading(false);
      return;
    }

    // Note: Validation conflicts are now handled by disabling the submit button
    // No need to check here since the button is disabled when validationErrors exists

    // Clear any existing errors
    setErrors({});

    // Create public booking data if applicable
    const publicData = isPublic ? {
      max: publicSpaces,
      fee: Object.entries(publicFees)
        .filter(([_, fee]) => fee !== '' && fee !== null && fee !== undefined)
        .reduce((acc, [userTypeId, fee]) => {
          const feeValue = parseInt(fee);
          if (!isNaN(feeValue)) {
            acc[userTypeId] = feeValue;
          }
          return acc;
        }, {}),
      // Preserve existing joiners when editing
      confirmed: isEditing && slotData.public ? (() => {
        try {
          const existingPublic = typeof slotData.public === 'string' ? JSON.parse(slotData.public) : slotData.public;
          return existingPublic.confirmed || {};
        } catch {
          return {};
        }
      })() : {},
      pending: isEditing && slotData.public ? (() => {
        try {
          const existingPublic = typeof slotData.public === 'string' ? JSON.parse(slotData.public) : slotData.public;
          return existingPublic.pending || {};
        } catch {
          return {};
        }
      })() : {}
    } : (isIndividualEditMode && existingPublicData ? {
      // In individual edit mode, preserve existing public data even if not making it public
      max: existingPublicData.max || 1,
      fee: existingPublicData.fee || {},
      confirmed: existingPublicData.confirmed || {},
      pending: existingPublicData.pending || {}
    } : null);

    // Determine if this is a "Never Ending" (block) booking
    const isBlockBooking = isRecurring && recurringDuration === -1;
    
    // Build recurring rule
    let recurringRule = null;
    if (!isIndividualEditMode && !(isEditing && hasJoinersInGroup) && isRecurring) {
      // For BLOCK bookings being edited, preserve the original startDate from the recurringRule
      // For new BLOCK bookings, use slotData.date (which will be the date selected)
      let startDateForRule = slotData.date;
      
      if (isEditing && isBlockBooking && slotData.recurringRule) {
        try {
          const originalRule = typeof slotData.recurringRule === 'string' 
            ? JSON.parse(slotData.recurringRule) 
            : slotData.recurringRule;
          // Use the original startDate if it exists (it should be a date string, not 'BLOCK')
          if (originalRule.startDate && originalRule.startDate !== 'BLOCK') {
            startDateForRule = originalRule.startDate;
          }
          // If slotData.date is 'BLOCK' and we don't have a valid startDate, we have a problem
          // This shouldn't happen, but if it does, we'll use slotData.date
        } catch (e) {
          console.error('Error parsing original recurringRule:', e);
        }
      }
      
      recurringRule = {
        frequency: recurringFrequency,
        duration: recurringDuration,
        startDate: startDateForRule
      };
      
      // If weekly and block booking, add day of week
      if (isBlockBooking && recurringFrequency === 'weekly') {
        // For weekly BLOCK bookings, preserve the day from the original rule if editing
        if (isEditing && slotData.recurringRule) {
          try {
            const originalRule = typeof slotData.recurringRule === 'string' 
              ? JSON.parse(slotData.recurringRule) 
              : slotData.recurringRule;
            if (originalRule.day) {
              recurringRule.day = originalRule.day;
            } else {
              // Fallback: calculate from startDate
              const startDate = new Date(startDateForRule);
              const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const dayOfWeek = dayNames[startDate.getDay()];
              recurringRule.day = dayOfWeek;
            }
          } catch (e) {
            console.error('Error parsing original recurringRule for day:', e);
            // Fallback: calculate from startDate
            const startDate = new Date(startDateForRule);
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayOfWeek = dayNames[startDate.getDay()];
            recurringRule.day = dayOfWeek;
          }
        } else {
          // For new weekly BLOCK bookings, calculate day from startDate
          const startDate = new Date(startDateForRule);
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const dayOfWeek = dayNames[startDate.getDay()];
          recurringRule.day = dayOfWeek;
        }
      }
    }

    // Create a single booking with full start and end time
    const bookingPayload = {
      isEditing,
      editMode: isIndividualEditMode ? 'individual' : 'full',
      courts: selectedCourts,
      bookingDate: isBlockBooking ? 'BLOCK' : slotData.date,
      startTime: slotData.startTime,
      endTime: endTime,
      categoryId,
      comments,
      displayLabel,
      isRecurring: (isIndividualEditMode || (isEditing && hasJoinersInGroup)) ? false : isRecurring, // Force non-recurring when joiners exist to prevent copying joiners to new series
      recurringRule: recurringRule,
      public: publicData,
      options: selectedExtras.filter(extra => {
        const qtyValue = parseInt(extra.quantity ?? 0, 10);
        return !isNaN(qtyValue) && qtyValue > 0;
      }),
      // Include original booking data for editing
      originalBooking: isEditing ? {
        bookingId: slotData.bookingId,
        originalCourts: slotData.court,
        originalPublic: slotData.public,
        recurringRule: slotData.recurringRule // Include recurringRule for BLOCK booking identification
      } : null
    };

    // Debugging: Log the booking payload before submission
    console.log('Submitting Booking Payload:', bookingPayload);

    try {
      await onSubmit(bookingPayload);
    } catch (error) {
      console.error('Error creating booking:', error);
      // Extract error message from API error object
      let errorMessage = 'An error occurred while processing your booking. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.type && error.message) {
        errorMessage = error.message;
      }
      
      // Set error state to display to user
      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const { court: slotCourt, date, startTime } = slotData;

  // Format the date using local time directly
  // For BLOCK bookings, use the displayDate if available, otherwise use the date
  // When editing a BLOCK booking instance, slotData.date is 'BLOCK' but we need the actual date for display
  const dateForDisplay = slotData.displayDate || slotData.date;
  const formattedDate = dateForDisplay && dateForDisplay !== 'BLOCK' 
    ? new Date(dateForDisplay).toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : 'BLOCK Booking';

  // Format time for display
  const formatTimeForDisplay = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0); // Set local time directly
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    try {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2
      }).format(numericValue);
    } catch {
      return `R${numericValue.toFixed(2)}`;
    }
  };

  const formatPriceCompact = (value) => {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) return '0';
    return numericValue % 1 === 0 ? `${numericValue}` : numericValue.toFixed(2);
  };

  const formatSignedPriceCompact = (value) => {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) return 'R0';
    const absValue = Math.abs(numericValue);
    const sign = numericValue < 0 ? '-' : '';
    return `${sign}R${formatPriceCompact(absValue)}`;
  };

  const userCredits = Number(user?.credits ?? 0) || 0;
  const feeForCreditsDisplay = isEditing ? Math.max(0, effectiveUpdateFee) : bookingPrice;
  const showCreditsInfo = feeForCreditsDisplay > 0;

  const formatExtraFee = (fee) => {
    const value = parseFloat(fee ?? 0);
    const safeValue = isNaN(value) ? 0 : value;
    return `R${safeValue.toFixed(2)}`;
  };

  // Use local times directly for display
  const localStartTime = slotData.startTime;
  const localEndTime = endTime;

  // Generate available end times (from start time + 1 hour up to 21:00 local time)
  const generateEndTimes = () => {
    if (courts.length === 0) return []; // No courts available

    // Use the selected court from the slotData
    const selectedCourtId = Array.isArray(slotData.court) ? slotData.court[0] : slotData.court; // Get the first court if it's an array
    const selectedCourt = courts.find(court => court.court === selectedCourtId); // Find the selected court

    if (!selectedCourt) return []; // If the court is not found, return empty

    const openingTime = selectedCourt.openingTime; // e.g., '06:00'
    const closingTime = selectedCourt.closingTime; // e.g., '22:00'
    const slotDuration = parseInt(selectedCourt.slotDuration, 10); // e.g., 30 or 60

    const startHour = parseInt(startTime.split(':')[0]);
    const startMinute = parseInt(startTime.split(':')[1]);
    const endTimes = [];
    let currentTime = new Date(`1970-01-01T${openingTime}:00`); // Use a fixed date

    // Calculate the maximum hour based on closingTime
    const maxEndHour = parseInt(closingTime.split(':')[0], 10);
    const maxEndMinute = parseInt(closingTime.split(':')[1], 10);

    // Generate end times in increments of slotDuration
    while (currentTime.getHours() < maxEndHour || (currentTime.getHours() === maxEndHour && currentTime.getMinutes() <= maxEndMinute)) {
      const formattedTime = currentTime.toTimeString().slice(0, 5); // Format as HH:MM
      if (currentTime.getHours() > startHour || (currentTime.getHours() === startHour && currentTime.getMinutes() > startMinute)) {
        endTimes.push(formattedTime);
      }
      currentTime.setMinutes(currentTime.getMinutes() + slotDuration); // Increment by slot duration
    }

    return endTimes;
  };

  const availableEndTimes = generateEndTimes();

  // Handle court selection/deselection
  const toggleCourt = (courtId) => {
    console.log('Toggling Court:', courtId);
    setSelectedCourts(prev => {
      const courtIdString = String(courtId);
      if (prev.includes(courtIdString)) {
        return prev.filter(id => id !== courtIdString);
      }
      return [...prev, courtIdString].sort();
    });
    // Clear submit errors when user changes court selection
    if (errors.submit) {
      setErrors({});
    }
    console.log('Updated Selected Courts:', selectedCourts);
  };

  console.log('Selected Courts:', selectedCourts);
  console.log('Slot Data in BookingModal:', slotData);
  const hasSelectedCourts = Array.isArray(selectedCourts) && selectedCourts.length > 0;
  const isCourtSelectionInvalid = !hasSelectedCourts;
  const categoryStyleCourt = selectedCourts[0] || court;

  // Function to generate duration options based on frequency
  const generateDurationOptions = (frequency) => {
    const durationOption = canBookRecurring.find(option => option.frequency === frequency);
    if (!durationOption) return [];
    
    const options = [];
    
    // If block is enabled, add "Never Ending" (-1) as the first option
    if (durationOption.block) {
      options.push(-1);
    }
    
    // Add regular duration options from 2 to maxDuration
    const maxDuration = durationOption.duration || 0;
    if (maxDuration > 0) {
      const regularOptions = Array.from({ length: maxDuration - 1 }, (_, i) => i + 2);
      options.push(...regularOptions);
    }
    
    return options;
  };

  const dialogZClass = previewMode ? 'z-[70]' : 'z-50';

  return (
    <Dialog open={isOpen} onClose={onClose} className={`fixed inset-0 overflow-y-auto ${dialogZClass}`}>
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
        <Dialog.Panel className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-4">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-600 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <Dialog.Title className="text-xl font-semibold text-slate-800 mb-4">
            {sportDisplayName} Booking
            {previewMode && (
              <span className="mt-2 block text-xs font-normal text-amber-800">
                Preview of the {sportDisplayName} booking form for{' '}
                {previewUserTypeName.trim() || 'this user type'}
              </span>
            )}
          </Dialog.Title>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
              <div className="flex items-stretch justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-slate-800">
                      {(() => {
                        if (!hasSelectedCourts) {
                          return '-';
                        }
                        const aliasLabels = selectedCourts.map((courtId) => getCourtDisplayNameById(courtId, courts, '').trim());
                        const hasAnyAlias = aliasLabels.some(Boolean);
                        if (!hasAnyAlias) {
                          const courtType = selectedCourts[0].split('-')[0];
                          const sportName = formatSportDisplayName(courtType);
                          const displayName = uniqueSportsCount === 1 ? 'Court' : sportName;
                          if (selectedCourts.length === 1) {
                            return `${displayName} ${selectedCourts[0].split('-')[1]}`;
                          }
                          const pluralName = uniqueSportsCount === 1 ? 'Courts' : sportName;
                          return `${pluralName} ${selectedCourts.map(c => c.split('-')[1]).join(', ')}`;
                        }
                        const fallbackPrefix = uniqueSportsCount === 1 ? 'Court' : formatSportDisplayName(selectedCourts[0].split('-')[0]);
                        const labels = selectedCourts.map((courtId, idx) => aliasLabels[idx] || getCourtDisplayNameById(courtId, courts, fallbackPrefix));
                        return labels.length === 1 ? labels[0] : labels.join(', ');
                      })()}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-800">{formattedDate}</p>
                  <p className="text-sm text-slate-800">
                    {formatTimeForDisplay(localStartTime)} - {formatTimeForDisplay(localEndTime)}
                  </p>
                  {smartLightsMessage && (
                    <p className="text-sm text-blue-600 mt-1">
                      {smartLightsMessage}
                    </p>
                  )}
                </div>

                <div className="flex flex-col justify-between items-end self-stretch">
                  <div className={`px-3 py-2 mt-1 border text-sm font-medium rounded-lg ${getCategoryStyleForModal({ 
                    category: selectedCategoryName, 
                    court: categoryStyleCourt
                  })}`}>
                    {selectedCategoryName}
                  </div>

                  {smartLightsMessage && !previewMode && (
                    <div className="relative inline-block mt-auto">
                      <Tooltip content="View light credits information">
                        <button
                          type="button"
                          onClick={() => setShowLightCreditsModal(true)}
                          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 512 513" fill="currentColor">
                            <path d="M211.468658,513.000000 C209.470093,510.751587 206.963058,509.604340 205.083267,507.772552 C199.739883,502.565613 197.474075,491.771423 200.801132,485.021301 C204.556931,477.401367 210.367172,472.559052 219.569275,472.611816 C244.380920,472.754181 269.194305,472.739624 294.006165,472.614563 C302.841400,472.570038 308.977325,476.819855 312.818207,484.227509 C316.576080,491.475006 314.527069,501.370880 308.769958,507.285309 C307.032928,509.069794 305.163086,510.725006 303.177002,512.719727 C272.645782,513.000000 242.291550,513.000000 211.468658,513.000000 M269.499939,495.660767 C275.990784,495.658325 282.482910,495.731659 288.971832,495.618317 C291.796906,495.568970 296.227966,496.528320 296.077271,492.450134 C295.946716,488.917450 291.644623,489.354034 288.690613,489.352783 C267.054504,489.343445 245.418381,489.352631 223.782272,489.368958 C222.622345,489.369843 221.413971,489.303772 220.314377,489.594757 C218.780441,490.000671 217.613083,491.084473 217.566971,492.757446 C217.514420,494.663574 219.094589,495.222076 220.579132,495.491882 C221.873764,495.727203 223.231842,495.647430 224.561935,495.648376 C239.207932,495.658997 253.853912,495.658142 269.499939,495.660767z"/>
                            <path d="M261.545898,1.000000 C265.369232,4.549515 265.431366,8.912919 265.397644,13.464887 C265.274414,30.088778 265.367798,46.714165 265.336914,63.338909 C265.325104,69.703064 263.399475,71.941475 257.520386,72.691536 C252.793198,73.294640 249.587357,70.790161 248.210724,65.224396 C247.818283,63.637730 247.679489,61.952053 247.675644,60.311192 C247.638153,44.351471 247.664734,28.391619 247.647400,12.431831 C247.642929,8.312404 248.517197,4.552659 251.833359,1.310972 C255.030594,1.000000 258.061188,1.000000 261.545898,1.000000z"/>
                            <path d="M185.532120,376.400208 C182.250870,358.780396 172.884430,345.464447 160.560440,333.299011 C141.411087,314.396027 131.107208,290.632019 126.705376,264.300598 C122.053497,236.473480 126.315987,209.800354 138.314713,184.396240 C142.431107,175.680862 147.793106,167.630524 153.836639,160.072113 C166.825531,143.827377 182.855576,131.437210 201.662292,122.693352 C214.215012,116.857170 227.412033,113.250198 241.214417,111.561821 C268.208862,108.259705 293.763580,112.969398 317.941956,125.033951 C328.042145,130.073746 337.153656,136.697189 345.634735,144.300125 C368.188202,164.518311 382.257263,189.418411 387.562622,219.142975 C392.116913,244.659500 389.650543,269.524200 379.652344,293.608582 C373.259552,309.007965 364.483734,322.757141 352.542145,334.368195 C341.717041,344.893707 333.455627,356.882751 330.098114,371.791626 C327.655365,382.638580 326.116302,393.604492 326.302856,404.787598 C326.465149,414.515839 323.593964,417.339081 314.002716,417.343628 C276.181122,417.361572 238.359558,417.363373 200.537979,417.341309 C190.714890,417.335571 187.519043,414.268250 187.696686,404.555603 C187.867294,395.226929 187.232834,385.992889 185.532120,376.400208 M148.880768,204.273453 C143.332047,221.185608 140.778687,238.357498 143.251129,256.206360 C146.705276,281.142334 156.527267,302.911163 174.186661,320.987457 C179.178574,326.097198 184.378571,331.042175 188.381287,336.991241 C199.954681,354.192230 203.934372,373.726440 205.241211,393.954498 C205.487274,397.763123 206.768204,398.767212 210.313629,398.714142 C220.975769,398.554504 231.641800,398.656830 242.306320,398.656372 C262.468933,398.655487 282.631775,398.604034 302.793915,398.699524 C306.391235,398.716583 308.489746,398.350311 308.955902,393.801819 C309.662201,386.910431 310.159821,379.978363 311.539337,373.120300 C314.949921,356.165161 321.130554,340.350647 333.381897,328.098541 C342.976807,318.503021 351.461029,308.487946 357.841187,296.494171 C370.739319,272.247650 374.948517,246.562195 369.247986,219.890808 C361.589142,184.056946 341.017059,157.228851 308.446472,140.336716 C284.303619,127.815475 258.435486,125.161026 231.929153,130.717331 C209.905914,135.333862 191.088348,146.446396 175.257690,162.176285 C163.493652,173.865433 155.022644,187.963715 148.880768,204.273453z"/>
                            <path d="M281.999878,466.374023 C257.011444,466.369080 232.522049,466.483307 208.035233,466.281494 C201.221909,466.225372 194.524323,465.285858 189.454773,459.570221 C183.428574,452.776093 181.746826,445.096252 185.169250,436.944519 C188.525070,428.951477 195.041031,424.663971 203.992752,424.664551 C238.976212,424.666901 273.959717,424.645386 308.943146,424.674194 C320.344818,424.683563 329.510681,432.787720 330.307648,443.418488 C331.140137,454.522491 323.978943,463.166595 312.367615,465.402496 C302.444183,467.313324 292.449493,465.907227 281.999878,466.374023 M249.500122,441.351593 C236.027222,441.355774 222.553711,441.292694 209.081955,441.419220 C206.293320,441.445374 201.976181,441.002686 202.030823,444.770508 C202.082184,448.311829 206.209579,447.647125 208.963531,447.648529 C240.732956,447.664673 272.502472,447.688599 304.271637,447.589233 C306.805847,447.581329 311.128357,449.051575 311.217041,445.079651 C311.310425,440.895630 306.959595,441.389648 303.892670,441.382172 C286.095245,441.338745 268.297668,441.355225 249.500122,441.351593z"/>
                            <path d="M123.176239,50.133133 C122.517883,45.282703 121.647835,40.559044 126.665848,37.904766 C131.587769,35.301304 136.711563,36.886719 139.992218,42.009712 C149.271729,56.500328 158.486496,71.032906 167.640579,85.603088 C170.718369,90.501915 169.647903,96.599968 165.483398,99.378403 C161.745972,101.871895 154.997299,100.488213 152.274643,96.236801 C142.548691,81.049789 132.994095,65.753036 123.176239,50.133133z"/>
                            <path d="M44.299664,136.298462 C41.639450,131.972565 42.526615,128.422775 45.519920,125.097664 C48.443245,121.850281 52.036919,122.059029 55.563141,123.396805 C63.803326,126.522942 71.065071,131.595261 78.909935,135.531097 C86.612053,139.395325 94.118065,143.651962 101.686005,147.780640 C102.986542,148.490158 104.296120,149.279907 105.377914,150.275543 C108.717918,153.349518 109.419472,159.596558 106.941238,163.086227 C104.772644,166.139893 99.660660,167.407120 95.635872,165.308136 C85.192871,159.861938 74.880157,154.165497 64.526466,148.548630 C59.569656,145.859573 54.664055,143.074982 49.681419,140.435287 C47.750156,139.412140 45.885937,138.378067 44.299664,136.298462z"/>
                            <path d="M410.782410,165.721100 C403.597076,160.784241 403.659760,152.576035 410.890839,148.611572 C426.168671,140.235413 441.573273,132.087051 457.011993,124.009781 C462.102539,121.346481 467.905029,122.566124 469.989807,126.227898 C472.873840,131.293427 471.617706,136.124588 465.579590,139.588913 C454.223114,146.104675 442.612762,152.178284 431.102539,158.425720 C427.170380,160.559998 423.207794,162.638565 419.285919,164.791443 C416.699860,166.211060 414.035156,166.809967 410.782410,165.721100z"/>
                            <path d="M394.807678,45.612587 C395.783936,48.747250 395.075775,51.137138 393.494812,53.496231 C383.712585,68.092957 374.040985,82.763908 364.245117,97.351433 C360.025848,103.634544 353.621124,104.512383 349.359650,99.353706 C346.325989,95.681358 346.892242,91.358162 349.234070,87.763809 C358.923340,72.892365 368.889801,58.201485 378.753937,43.443939 C381.097717,39.937477 384.553253,39.182278 388.399506,39.754993 C391.574310,40.227734 394.032715,41.733952 394.807678,45.612587z"/>
                            <path d="M304.978943,177.086609 C290.791962,167.789917 275.759094,162.421921 258.953400,162.262268 C250.741562,162.184265 246.449966,156.502792 248.731094,149.729874 C249.917816,146.206421 252.365524,144.848236 255.872665,144.672592 C271.256226,143.902222 285.781128,147.027496 299.832672,153.354492 C311.669281,158.684174 322.263519,165.759048 331.112518,175.068420 C347.980316,192.813721 358.251953,213.710663 359.386444,238.586365 C359.565063,242.502716 361.129272,246.298813 359.448364,250.340012 C357.718750,254.498413 354.487488,256.389526 350.499237,256.085510 C346.250153,255.761581 343.093872,253.370346 342.678040,248.513000 C341.601654,235.940018 340.102783,223.395721 334.520630,211.884537 C327.743530,197.909271 318.262482,186.081284 304.978943,177.086609z"/>
                          </svg>
                        </button>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {canBookMultipleCourts && (
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-2">
                  {hasAnyDisplayNameInSport ? 'Selected Resources' : 'Selected Courts'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {courts.map(court => {
                    const aliasLabel = getCourtDisplayNameById(court.court, courts, '').trim();
                    const hasAlias = Boolean(aliasLabel);
                    return (
                      <button
                        key={court.court}
                        type="button"
                        onClick={() => toggleCourt(court.court)}
                        className={`${hasAlias ? 'min-h-10 px-2 text-xs' : 'w-10 h-10'} flex items-center justify-center rounded-lg border transition-colors ${
                          selectedCourts.includes(court.court)
                            ? getCategoryStyleForModal({ 
                              category: 'Available', 
                              court: court.court 
                            })
                            : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                        }`}
                        title={getCourtLabel(court.court)}
                      >
                        {hasAlias ? aliasLabel : court.court.split('-')[1]}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-800 mb-2">Category</label>
              <div className="grid grid-cols-2 gap-2">
                {sportCategories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategoryId(cat.id)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${getCategoryStyleForModal({ 
                      category: cat.name, 
                      court: categoryStyleCourt
                    })}`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-800">Date & Time</label>
                {canBookRecurring.length > 0 && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="recurring"
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                      disabled={isIndividualEditMode || (isEditing && hasJoinersInGroup)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <label htmlFor="recurring" className={`text-sm ${(isIndividualEditMode || (isEditing && hasJoinersInGroup)) ? 'text-slate-600' : 'text-slate-600'}`}>
                      Recurring
                    </label>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Start Time</label>
                  <input
                    type="text"
                    value={formatTimeForDisplay(localStartTime)}
                    readOnly
                    className="block h-10 w-full px-4 py-2 text-slate-600 bg-slate-50 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">End Time</label>
                  <select
                    value={endTime}
                    onChange={(e) => {
                      setEndTime(e.target.value);
                      // Clear submit errors when user changes end time
                      if (errors.submit) {
                        setErrors({});
                      }
                    }}
                    className="block h-10 w-full px-4 py-2 text-slate-600 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  >
                    {availableEndTimes.map(time => (
                      <option key={time} value={time}>
                        {formatTimeForDisplay(time)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {canBookRecurring.length > 0 && isRecurring && !isIndividualEditMode && !(isEditing && hasJoinersInGroup) && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <label className="block text-sm font-medium text-slate-800 mb-2">Repeat</label>
                <div className="grid grid-cols-2 gap-4">
                  <select
                    value={recurringFrequency}
                    onChange={(e) => {
                      const newFrequency = e.target.value;
                      setRecurringFrequency(newFrequency);
                      // Reset the duration to a valid default for the newly selected
                      // frequency. Otherwise a stale duration (e.g. -1 / Never Ending
                      // from weekly) can leave the dropdown showing one value while the
                      // state holds another, so the price wouldn't update until the user
                      // manually re-picked a duration.
                      const availableOptions = generateDurationOptions(newFrequency);
                      const defaultDuration = getInitialDuration(newFrequency);
                      const nextDuration = availableOptions.includes(defaultDuration)
                        ? defaultDuration
                        : (availableOptions.length > 0 ? availableOptions[0] : defaultDuration);
                      setRecurringDuration(nextDuration);
                    }}
                    className="block h-10 w-full px-4 py-2 text-slate-600 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  >
                    {canBookRecurring.map(option => (
                      <option key={option.frequency} value={option.frequency}>
                        {option.frequency.charAt(0).toUpperCase() + option.frequency.slice(1)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={recurringDuration}
                    onChange={(e) => setRecurringDuration(parseInt(e.target.value))}
                    className="block h-10 w-full px-4 py-2 text-slate-600 bg-white border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  >
                    {generateDurationOptions(recurringFrequency).map(option => (
                      <option key={option} value={option}>
                        {option === -1 ? 'Never Ending' : `${option} ${recurringFrequency === 'weekly' ? 'week' : 'day'}${option > 1 ? 's' : ''}`}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {recurringDuration === -1 
                    ? `This booking will repeat ${recurringFrequency === 'daily' ? 'every day' : 'every week'} at the same time indefinitely`
                    : `This booking will repeat ${recurringFrequency === 'daily' ? 'every day' : 'every week'} at the same time for ${recurringDuration} ${recurringFrequency === 'weekly' ? 'week' : 'day'}${recurringDuration > 1 ? 's' : ''}`
                  }
                </p>
              </div>
            )}

            {isIndividualEditMode && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center mb-2">
                  <svg className="h-5 w-5 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <label className="block text-sm font-medium text-yellow-800">Individual Booking Edit</label>
                </div>
                <p className="text-sm text-yellow-700">
                  This booking has joiners, so you can only edit this individual booking. Recurring settings are disabled to preserve joined users.
                </p>
              </div>
            )}

            {isEditing && hasJoinersInGroup && !isIndividualEditMode && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center mb-2">
                  <svg className="h-5 w-5 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <label className="block text-sm font-medium text-yellow-800">Same-Day Multi-Court Edit</label>
                </div>
                <p className="text-sm text-yellow-700">
                  This booking has joiners, so you can edit all courts for this specific day but recurring settings are disabled to prevent copying joiners to new bookings in the series.
                </p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-800">Calendar Booking Label</label>
                {canMakePublicBookings && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="public"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      disabled={hasExistingJoiners}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <label htmlFor="public" className="text-sm text-slate-600">
                      Public
                    </label>
                  </div>
                )}
              </div>
              <input
                type="text"
                value={displayLabel}
                onChange={(e) => {
                  setDisplayLabel(e.target.value);
                  // Clear errors when user starts typing
                  if (errors.displayLabel || errors.submit) {
                    setErrors({});
                  }
                }}
                className={`block w-full px-4 py-2 text-slate-600 border ${errors.displayLabel ? 'border-red-500' : 'border-slate-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition`}
                placeholder="Enter calendar booking display label"
              />
              {errors.displayLabel && (
                <p className="mt-1 text-xs text-red-500">{errors.displayLabel}</p>
              )}
            </div>

            {/* Show validation errors for conflicts */}
            {validationErrors && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex items-center mb-2">
                  <svg className="h-5 w-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <label className="block text-sm font-medium text-red-800">Time Conflicts Detected</label>
                </div>
                <div className="space-y-2">
                  {validationErrors.map((conflict, index) => (
                    <div key={index} className="text-sm text-red-700">
                      <strong>{getCourtLabel(conflict.court)}:</strong> Conflicts with {conflict.conflictingBooking.createdByName}'s booking from {conflict.conflictingBooking.startTime} to {conflict.conflictingBooking.endTime}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isCourtSelectionInvalid && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex items-center mb-2">
                  <svg className="h-5 w-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <label className="block text-sm font-medium text-red-800">
                    {hasAnyDisplayNameInSport ? 'Resource Selection Required' : 'Court Selection Required'}
                  </label>
                </div>
                <p className="text-sm text-red-700">
                  {hasAnyDisplayNameInSport
                    ? 'Select at least one resource to continue.'
                    : 'Select at least one court to continue.'}
                </p>
              </div>
            )}

            {blockFeeRestriction && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center mb-2">
                  <svg className="h-5 w-5 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <label className="block text-sm font-medium text-yellow-800">
                    Never Ending bookings not available for paid courts
                  </label>
                </div>
                <p className="text-sm text-yellow-700">
                  This booking has an associated fee and cannot be set to Never Ending. Please choose a specific duration for recurring bookings.
                </p>
              </div>
            )}

            {/* Show submission errors */}
            {errors.submit && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex items-center mb-2">
                  <svg className="h-5 w-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <label className="block text-sm font-medium text-red-800">Booking Error</label>
                </div>
                <p className="text-sm text-red-700">{errors.submit}</p>
              </div>
            )}

            {canMakePublicBookings && isPublic && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-medium text-slate-800">
                    Number of Spaces
                    {hasExistingJoiners && (
                      <span className="text-xs text-slate-800 ml-2">
                        ({currentJoiners} joined already)
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    value={publicSpaces}
                    onChange={(e) => setPublicSpaces(Math.max(hasExistingJoiners ? currentJoiners : 1, parseInt(e.target.value) || 1))}
                    className="w-20 px-2 py-1 text-sm text-slate-600 border border-slate-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    min={hasExistingJoiners ? currentJoiners : 1}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-2">Joining Fees by User Type</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {userTypes
                      .sort((a, b) => parseInt(a.id) - parseInt(b.id))
                      .map(userType => (
                        <div key={userType.id} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600 flex-1">{userType.name}</span>
                          <input
                            type="number"
                            value={publicFees[userType.id] !== undefined ? publicFees[userType.id] : ''}
                            onChange={(e) => setPublicFees(prev => ({
                              ...prev,
                              [userType.id]: e.target.value
                            }))}
                            className="w-20 px-2 py-1 text-sm text-slate-600 border border-slate-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            min="0"
                            placeholder="Not allowed"
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-800 mb-2">Notes (optional)</label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={2}
                className="block w-full px-4 py-2 text-slate-600 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                placeholder="Add any additional notes here..."
              />
            </div>

            {canBookExtras && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <label className="block text-sm font-medium text-slate-800 mb-2">Extra Options</label>
                {availableExtras.length === 0 ? (
                  <p className="text-xs text-slate-600">
                    No Booking Extras are configured for {sportDisplayName}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {availableExtras.map(extra => {
                      const selectedExtra = selectedExtras.find(item => item.option === extra.option);
                      return (
                        <div key={extra.option} className="flex items-center justify-between gap-3">
                          <label className="flex items-center text-sm text-slate-600 flex-1 min-w-0">
                            <input
                              type="checkbox"
                              checked={Boolean(selectedExtra)}
                              onChange={(e) => handleExtraToggle(extra.option, e.target.checked)}
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 truncate">
                              {extra.name} ({formatExtraFee(extra.fee)})
                            </span>
                          </label>
                          {selectedExtra && (
                            <input
                              type="number"
                              value={selectedExtra.quantity === '' ? '' : (selectedExtra.quantity ?? 1)}
                              onChange={(e) => handleExtraQuantityChange(extra.option, e.target.value)}
                              className="w-20 px-2 py-1 text-sm text-slate-600 border border-slate-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              min="1"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              placeholder="0"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex min-w-0 items-center justify-between gap-2 pt-1 sm:gap-3 border-slate-200">
              <div className="min-w-0 flex-1 pr-1 text-xs leading-tight text-blue-600 sm:text-sm sm:leading-normal">
                {showCreditsInfo && (
                  <span className="tabular-nums">
                    Credits: {formatCurrency(userCredits)}
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap ${
                    previewMode || isLoading || validationErrors || blockFeeRestriction || isCourtSelectionInvalid
                      ? 'bg-blue-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  disabled={previewMode || isLoading || validationErrors || blockFeeRestriction || isCourtSelectionInvalid}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {isEditing ? 'Updating...' : 'Booking...'}
                    </>
                  ) : isEditing ? (
                    `Update${effectiveUpdateFee !== 0 ? ` ${formatSignedPriceCompact(effectiveUpdateFee)}` : ''}`
                  ) : (
                    `Book${bookingPrice > 0 ? ` R${formatPriceCompact(bookingPrice)}` : ''}`
                  )}
                </button>
              </div>
            </div>
          </form>
        </Dialog.Panel>
      </div>

      <LightCreditsModal
        isOpen={showLightCreditsModal && !previewMode}
        onClose={() => setShowLightCreditsModal(false)}
        user={user}
        courtData={courtData}
        onSubmit={(amount) => {
          if (!user) return;
          
          const reference = buildCreditReference(user.firstName, user.lastName);
          
          const paymentDetails = {
            totalPaymentAmount: amount,
            reference,
            userUuid: user.uuid,
            userDisplayName: `${user.firstName} ${user.lastName}`,
            type: 'credit_purchase',
            creditAmount: amount
          };
          
          creditPaymentDetailsRef.current = paymentDetails;
          setCreditPaymentDetails(paymentDetails);
          setShowCreditPaymentModal(true);
        }}
      />

      <PaymentModal
        isOpen={showCreditPaymentModal && !previewMode}
        onClose={handleCreditPaymentModalClose}
        totalPaymentAmount={creditPaymentDetails?.totalPaymentAmount || 0}
        reference={creditPaymentDetails?.reference || ''}
        onPaymentComplete={handleCreditPaymentComplete}
        paymentDetails={creditPaymentDetails}
        customText={`
          <ul class="list-disc pl-5">
            <li>Do not close this window until payment completes</li>
            <li>Payment reference: <span class="font-bold">${creditPaymentDetails?.reference || ''}</span></li>
          </ul>
        `}
        showTimer={false}
      />
    </Dialog>
  );
};

export default BookingModal;
